import { Add, Delete } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import AddCompanyJobCompetency from "./partials/AddCompanyJobCompetencies/AddCompanyJobCompetencies";
import DeleteCompanyJobCompetency from "./partials/DeleteCompanyJobCompetencies/DeleteCompanyJobCompetencies";
import {
  ICompanyJobCompetency,
  ICompanyJobCompetencyInput,
} from "models/CompanyJobCompetency";
import {
  CompanyJobCompetenciesCells,
  deleteCompanyJobCompetency,
  addCompanyJobCompetency,
  getCompanyJobCompetencies,
} from "framework/companyJobCompetencies";
import { useTranslation } from "react-i18next";
import { getCompetencies } from "framework/competencies";
import { useParams } from "react-router-dom";
import { ICompetency } from "models/competency";
import { getUserData } from "helpers";

interface Props {
  feature: Feature;
}

const CompanyJobCompetencies: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [CompanyJobCompetencies, setCompanyJobCompetencies] = useState<
    ICompanyJobCompetency[]
  >([] as ICompanyJobCompetency[]);
  const [FilterCompanyJobCompetencies, setFilterCompanyJobCompetencies] =
    useState<ICompanyJobCompetency[]>([] as ICompanyJobCompetency[]);
  const { t, i18n } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const user = getUserData();
  const { colors } = user;
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCompanyJobCompetency, setSelectedCompanyJobCompetency] =
    useState<ICompanyJobCompetency>({} as ICompanyJobCompetency);
  const [inputObj, setInputObj] = useState<ICompanyJobCompetencyInput>(
    {} as ICompanyJobCompetencyInput
  );
  const [Competencies, setCompetencies] = useState<ICompetency[]>(
    [] as ICompetency[]
  );
  const { setAlert } = useAlert();
  const urlParams = useParams();
  let id: string = urlParams[`${feature.singleName}Id`] as string;
  // eslint-disable-next-line
  const queryClient = new QueryClient();
  // alert('in competencies');
  const addCompetenciesMutation = useMutation({
    mutationFn: (createInput: ICompanyJobCompetencyInput) => {
      return addCompanyJobCompetency(createInput);
    },
  });

  const deleteCompetenciesMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCompanyJobCompetency(id);
    },
  });

  const fetchCompetencies = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery("get Competencies", () =>
        getCompetencies(`/filtered?id=${id}`)
      );

      setCompetencies(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery(
        "get CompanyJobCompetencies",
        () => getCompanyJobCompetencies(`/${id}`)
      );
      response =
        response && response.length > 0
          ? response.map((item: ICompanyJobCompetency) => ({
            id: item.id,
            competencyName: item.competencyName,
          }))
          : [];

      setCompanyJobCompetencies(response);
      setFilterCompanyJobCompetencies(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchCompetencies();
    // eslint-disable-next-line
  }, []);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    let competencies = value
      .map((competency: any) => competency.value)
      .join(";");
    setInputObj((prev) => ({
      ...prev,
      [name]: competencies,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Country: ICompanyJobCompetency) => {
    setSelectedCompanyJobCompetency(Country);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompanyJobCompetency({} as ICompanyJobCompetency);
  };

  const handleOpenDelete = (Country: ICompanyJobCompetency) => {
    setSelectedCompanyJobCompetency(Country);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompanyJobCompetency({} as ICompanyJobCompetency);
  };

  const getBodyCells = (row: ICompanyJobCompetency) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "competencyName")
        return {
          id: "competencyName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.competencyName}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                <Tooltip title={t("T_delete")}>
                  <IconButton
                    size={"small"}
                    color={"error"}
                    onClick={() => handleOpenDelete(row)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.competencies)
      return setAlert({
        open: true,
        message: "Please choose a competency",
        type: "error",
      });
    try {
      setLoading2(true);
      inputObj.companyJobId = id;
      await addCompetenciesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Competencies added successfully",
        type: "success",
      });
      fetchData();
      fetchCompetencies();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCompetenciesMutation.mutateAsync(
        selectedCompanyJobCompetency.id
      );
      setAlert({
        open: true,
        message: "Company Department deleted successfully",
        type: "success",
      });
      fetchData();
      fetchCompetencies();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompanyJobCompetencies(CompanyJobCompetencies);
    let filtered = CompanyJobCompetencies.filter((companyDepartment) =>
      companyDepartment.competencyName
        .toLowerCase()
        .includes(value.toLowerCase())
    );
    setFilterCompanyJobCompetencies(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            <Tooltip title="Add Company Job Competencies">
              <IconButton
                sx={{
                  // bgcolor: 'primary.main',
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "white",
                  },
                  height: 40,
                  width: 40,
                  transition: "0.5s",
                }}
                onClick={handleOpenAdd}
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>

        {FilterCompanyJobCompetencies.length > 0 && (
          <List
            headCells={CompanyJobCompetenciesCells}
            data={FilterCompanyJobCompetencies}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCompanyJobCompetencies ||
          FilterCompanyJobCompetencies.length === 0) && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_company_jobs_competencies_header")}
              message={t("T_no_company_jobs_competencies_message")}
            />
          )}
      </Stack>

      {openAdd && (
        <AddCompanyJobCompetency
          open={openAdd}
          loading={loading2}
          Competencies={Competencies}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCompanyJobCompetency
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default CompanyJobCompetencies;
